import Protected from "@/pages/protected";
import { DataService } from "@/services/data";
import { handle_error, handle_success } from "@/utils/wretch";
import { Outlet, RouteObject, defer, json } from "react-router-dom";

const FifplRoute: RouteObject = {
    path: "fifpl",
    element: (
        <Protected groups={["app_billing_admin", "team_finance", "team_support"]}>
            <Outlet />
        </Protected>
    ),
    children: [
        {
            index: true,
            async lazy() {
                let { ListPage } = await import("@/pages/billing/fifpl/list");
                return { Component: ListPage };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }
                if (!url.searchParams.has("include_closed_sessions")) {
                    url.searchParams.set("include_closed_sessions", "0");
                }
                const billing_sessions = await DataService.client
                    .url(`/billing/fifpl/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return { billing_sessions };
            },
            action: async ({ request }) => {
                const data = await request.json();
                try {
                    const content = await DataService.client.url(`/billing/fifpl/create_from_reference/`).post(data).json();
                    return json({ success: true, content });
                } catch (error: any) {
                    const message = error.json?.message || error.message;
                    if (error.response?.status === 403) {
                        return json({ success: false, message: "Vous n'avez pas la permission de faire cette action." });
                    }
                    return json({ success: false, message });
                }
            }
        },
        {
            path: "a-creer",
            async lazy() {
                let { ToCreatePage } = await import("@/pages/billing/fifpl/to_create");
                return { Component: ToCreatePage };
            },
            loader: async () => {
                const to_create = DataService.client.url(`/billing/fifpl/to_create/`).get().json();
                return defer({ to_create });
            }
        },
        {
            path: "a-creer/:fifpl_session",
            async lazy() {
                let { ToCreateDetailPage } = await import("@/pages/billing/fifpl/to_create_detail");
                return { Component: ToCreateDetailPage };
            },
            loader: async ({ params }) => {
                const to_create = DataService.client
                    .url(`/billing/fifpl/to_create/${encodeURIComponent(params.fifpl_session ?? "")}/`)
                    .get()
                    .json();
                return defer({ to_create });
            },
            action: async ({ params, request }) => {
                const data = await request.json();
                try {
                    const content = await DataService.client
                        .url(`/billing/fifpl/to_create/${encodeURIComponent(params.fifpl_session ?? "")}/build/`)
                        .post(data)
                        .json();
                    return json({ success: true, content: content });
                } catch (error: any) {
                    try {
                        if (error.response) {
                            switch (error.response.status) {
                                case 400:
                                    return json({ success: false, message: error.message });
                                case 401:
                                    return json({ success: false, message: "Veuillez vous reconnecter." });
                                case 403:
                                    return json({ success: false, message: "Vous n'avez pas la permission de faire cette action." });
                                case 404:
                                    return json({ success: false, message: "Cette action n'existe pas, veuillez contacter l'équipe technique." });
                                default:
                                    return json({
                                        success: false,
                                        message: `Veuillez contacter l'équipe technique (${error.response.status} ${error.response.statusText}).`
                                    });
                            }
                        }
                    } catch (_error) {
                        return json({ success: false, message: "Veuillez contacter l'équipe technique." }, { status: 500 });
                    }
                }
            }
        },
        {
            path: "participants/:sessionId",
            loader: async ({ params }) => {
                const sessionId = params.sessionId;
                try {
                    const participants = await DataService.client.url(`/registration/sales/?page_size=1000&session__id=${sessionId}`).get().json();
                    return handle_success(participants);
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            path: ":sessionId",
            async lazy() {
                let { DetailPage } = await import("@/pages/billing/fifpl/details");
                return { Component: DetailPage };
            },
            loader: async ({ params }) => {
                const sessionId = params.sessionId;
                const billing_session = await DataService.client.url(`/billing/fifpl/${sessionId}/`).get().json();
                const billing_session_data = DataService.client.url(`/billing/fifpl/${sessionId}/data/`).get().json();
                const billing_session_submissions = DataService.client.url(`/billing/fifpl/${sessionId}/submissions/`).get().json();
                return defer({ billing_session, billing_session_data, billing_session_submissions });
            },
            action: async ({ params, request }) => {
                const sessionId = params.sessionId;
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");

                if (action) {
                    try {
                        const content = await DataService.client.url(`/billing/fifpl/${sessionId}/${action}/`).post(data).json();
                        return handle_success(content);
                    } catch (error) {
                        return handle_error(error);
                    }
                } else {
                    try {
                        const content = await DataService.client.url(`/billing/fifpl/${sessionId}/`).patch(data).json();
                        return handle_success(content);
                    } catch (error) {
                        return handle_error(error);
                    }
                }
            },
            children: [
                {
                    path: ":submissionId",
                    loader: async ({ params }) => {
                        const submissionId = params.submissionId;
                        const billing_submission = await DataService.client.url(`/billing/fifpl_submissions/${submissionId}/`).get().json();
                        return { billing_submission };
                    },
                    action: async ({ params, request }) => {
                        const submissionId = params.submissionId;
                        const data = await request.json();
                        const url = new URL(request.url);
                        const action = url.searchParams.get("action");
                        if (action) {
                            try {
                                const content = await DataService.client.url(`/billing/fifpl_submissions/${submissionId}/${action}/`).post(data).json();
                                return handle_success(content);
                            } catch (error) {
                                return handle_error(error);
                            }
                        } else {
                            try {
                                const content = await DataService.client.url(`/billing/fifpl_submissions/${submissionId}/`).patch(data).json();
                                return handle_success(content);
                            } catch (error) {
                                return handle_error(error);
                            }
                        }
                    }
                }
            ]
        }
    ]
};
export default FifplRoute;
